import { Injectable } from '@angular/core';

const USER_KEY = 'auth-user';

export interface SessionUser {
  id: number;
  email: string;
  roleId: number;
  companyId: number;
}

interface DecodedToken {
  header: Record<string, unknown>;
  payload: {
    exp?: number;
    user?: {
      id?: number;
      email?: string;
      role_id?: number;
      company_id?: number;
    };
  };
  signature: string;
}

@Injectable({
  providedIn: 'root'
})

export class StorageService {

  email: string='';
  roleId: number=0;
  companyId: number=0;
  userId: number=0;

  constructor() { }

  clean(): void {
    window.localStorage.removeItem(USER_KEY);
    this.resetSession();
  }

  public saveUser(user: unknown): SessionUser | null {
    window.localStorage.removeItem(USER_KEY);
    window.localStorage.setItem(USER_KEY, JSON.stringify(user));
    return this.hydrateSession();
  }

  public getUser(): Record<string, unknown> {
    return this.readStoredUser() ?? {};
  }

  public isLoggedIn(): boolean {
    return this.getSessionUser() !== null;
  }

  public getToken(): { token?: string } {
    const token = this.readStoredUser()?.['token'];
    return typeof token === 'string' && token ? { token } : {};
  }

  public getRefreshToken(): string | null {
    const refreshToken = this.readStoredUser()?.['refreshToken'];
    return typeof refreshToken === 'string' && refreshToken ? refreshToken : null;
  }

  public getAccessTokenExpiresAt(): number | null {
    const expiresAt = Number(this.decodeJWT()?.payload.exp);
    return Number.isFinite(expiresAt) && expiresAt > 0 ? expiresAt * 1000 : null;
  }

  public decodeJWT(): DecodedToken | null {
    const token = this.getToken().token;
    if (token) {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      try {
        const header = this.decodeTokenPart(parts[0]);
        const payload = this.decodeTokenPart(parts[1]);
        return { header, payload, signature: parts[2] } as DecodedToken;
      } catch {
        return null;
      }
    }
    return null;
  }

  public getSessionUser(): SessionUser | null {
    const payload = this.decodeJWT()?.payload;
    const user = payload?.user;
    const id = Number(user?.id);
    const roleId = Number(user?.role_id);
    const companyId = Number(user?.company_id ?? 0);

    if (payload?.exp && payload.exp <= Date.now() / 1000) return null;
    if (!user || !Number.isInteger(id) || id <= 0 || !Number.isInteger(roleId) || roleId <= 0) return null;
    if (typeof user.email !== 'string' || !user.email.trim()) return null;

    return { id, email: user.email, roleId, companyId };
  }

  public hydrateSession(): SessionUser | null {
    const session = this.getSessionUser();
    if (!session) {
      const expiresAt = this.getAccessTokenExpiresAt();
      const canRefresh = !!expiresAt && expiresAt <= Date.now() && !!this.getRefreshToken();
      if (!canRefresh) {
        window.localStorage.removeItem(USER_KEY);
        this.resetSession();
      }
      return null;
    }

    this.email = session.email;
    this.roleId = session.roleId;
    this.companyId = session.roleId === 1 ? 0 : session.companyId;
    this.userId = session.id;
    return session;
  }

  public getWindowMode():any{
    const mode = window.localStorage.getItem("windowMode");

    if (mode) return mode;
    else return 'open';
  }

  public setWindowMode(mode:string){
    window.localStorage.setItem("windowMode", mode);
  }

  private readStoredUser(): Record<string, unknown> | null {
    const storedUser = window.localStorage.getItem(USER_KEY);
    if (!storedUser) return null;

    try {
      const parsed = JSON.parse(storedUser);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  private decodeTokenPart(value: string): Record<string, unknown> {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(padded), character => character.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  }

  private resetSession(): void {
    this.email = '';
    this.roleId = 0;
    this.companyId = 0;
    this.userId = 0;
  }
}
